import { isApiReachable } from "@grafit/frontend-auth/utils/is-api-reachable";
import { create } from "zustand";
import { isNetworkError, type TrpcErrorLike } from "./trpc-error-helpers";

export type ReachabilityFailureSource = "query" | "mutation";

interface BackendReachable {
  isBackendReachable: true;
  lastFailureSource: null;
  lastFailureId: number;
}

interface BackendUnreachable {
  isBackendReachable: false;
  lastFailureSource: ReachabilityFailureSource;
  lastFailureId: number;
}

type BackendReachabilityStore = (BackendReachable | BackendUnreachable) & {
  setIsBackendReachable: (isBackendReachable: boolean, source?: ReachabilityFailureSource) => void;
};

export const useBackendReachabilityStore = create<BackendReachabilityStore>()((set) => ({
  isBackendReachable: true,
  lastFailureSource: null,
  lastFailureId: 0,
  setIsBackendReachable: (isBackendReachable, source) =>
    set((state) => {
      if (isBackendReachable) {
        return {
          isBackendReachable,
          lastFailureSource: null,
          lastFailureId: state.lastFailureId,
        };
      }
      return {
        isBackendReachable: false,
        lastFailureSource: source,
        lastFailureId: state.lastFailureId + 1,
      };
    }),
}));

const setBackendUnreachable = (source: ReachabilityFailureSource): void =>
  useBackendReachabilityStore.getState().setIsBackendReachable(false, source);

export const setBackendReachable = (): void => {
  if (!useBackendReachabilityStore.getState().isBackendReachable) {
    useBackendReachabilityStore.getState().setIsBackendReachable(true);
  }
};

export const assessBackendReachability = (error: TrpcErrorLike, source: ReachabilityFailureSource): void => {
  if (isNetworkError(error)) {
    setBackendUnreachable(source);
  }
};

const POLL_INTERVAL_MS = 5000;
let pollIntervalId: ReturnType<typeof setInterval> | undefined;

const startReachabilityPolling = (): void => {
  if (pollIntervalId !== undefined) {
    return;
  }
  pollIntervalId = setInterval(() => {
    void (async () => {
      if (await isApiReachable()) {
        setBackendReachable();
      }
    })();
  }, POLL_INTERVAL_MS);
};

const stopReachabilityPolling = (): void => {
  if (pollIntervalId === undefined) {
    return;
  }
  clearInterval(pollIntervalId);
  pollIntervalId = undefined;
};

useBackendReachabilityStore.subscribe((state) => {
  if (state.isBackendReachable) {
    stopReachabilityPolling();
  } else {
    startReachabilityPolling();
  }
});
